import React, { useState, useEffect } from 'react';
import { mockMenuData } from '../mock';

const MenuSection = () => {
  const [activeTab, setActiveTab] = useState('soir');
  const [menuData, setMenuData] = useState(mockMenuData);
  const [cmsData, setCmsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        // Fetch CMS menus
        const cmsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/menus`);
        if (cmsResponse.ok) {
          const cms = await cmsResponse.json();
          setCmsData(cms);
        }
        
        // Also try legacy API
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menus/active`);
        if (response.ok) {
          const data = await response.json();
          if (data.soir || data.midi) {
            setMenuData(data);
          }
        }
      } catch (error) {
        console.log('Using fallback menu data');
      } finally {
        setLoading(false);
      }
    };
    fetchMenus();
  }, []);

  const currentMenu = menuData[activeTab] || mockMenuData[activeTab];

  // ==================== LUNCH MENU DATA ====================
  // Use CMS data if available, otherwise fallback to hardcoded
  const lunchMenus = {
    weeklyMenu: cmsData?.weekly_menu ? {
      title: cmsData.weekly_menu.title,
      price: cmsData.weekly_menu.menu_price,
      subtitle: cmsData.weekly_menu.subtitle,
      entree: {
        title: cmsData.weekly_menu.starter_title || "Entrée",
        name: cmsData.weekly_menu.starter_name,
        description: cmsData.weekly_menu.starter_description
      },
      plat: {
        title: cmsData.weekly_menu.main_title || "Plat",
        name: cmsData.weekly_menu.main_name,
        description: cmsData.weekly_menu.main_description
      },
      dessert: {
        title: cmsData.weekly_menu.dessert_title || "Dessert",
        name: cmsData.weekly_menu.dessert_name,
        description: cmsData.weekly_menu.dessert_description
      },
      note: cmsData.weekly_menu.note
    } : {
      title: "Menu de la semaine",
      price: "25€",
      subtitle: "Du mardi au vendredi midi",
      entree: {
        title: "Entrée",
        name: "Rémoulade de carottes, céleri-rave et pomme granny",
        description: "Graines de moutarde, herbes fraîches et magret fumé"
      },
      plat: {
        title: "Plat",
        name: "Poulet sauté façon chasseur, jus corsé",
        description: "Mousseline de panais et champignons de Paris"
      },
      dessert: {
        title: "Dessert",
        name: "Baba bouchon légèrement imbibé au rhum",
        description: "Chantilly vanille onctueuse"
      },
      note: "Ce menu évolue chaque semaine selon les produits de saison."
    },
    menuSource: cmsData?.menu_source ? {
      title: cmsData.menu_source.title,
      price: cmsData.menu_source.menu_price,
      subtitle: cmsData.menu_source.subtitle,
      entrees: cmsData.menu_source.starters?.length ? cmsData.menu_source.starters : [
        { name: "Suprêmes de cailles rôtis, servis tièdes", description: "Saladine de lentilles aux aromates, pickles d'oignons rouges, jus balsamique" }
      ],
      plats: cmsData.menu_source.mains?.length ? cmsData.menu_source.mains : [
        { name: "Risotto arborio crémeux au mascarpone", description: "Champignons de Paris, coulis de cresson et dentelle de parmesan" }
      ],
      desserts: cmsData.menu_source.desserts?.length ? cmsData.menu_source.desserts : [
        { name: "Brioche perdue croustillante et moelleuse", description: "Caramel au beurre salé, chantilly légère, glace noix de macadamia" }
      ]
    } : {
      title: "Menu Source",
      price: "35€",
      subtitle: "Uniquement le midi en semaine",
      entrees: [
        {
          name: "Suprêmes de cailles rôtis, servis tièdes",
          description: "Saladine de lentilles aux aromates, pickles d'oignons rouges, jus balsamique"
        },
        {
          name: "Velouté de potimarron comme un cappuccino",
          description: "Crème légère à la noisette et éclats de châtaignes"
        }
      ],
      plats: [
        {
          name: "Risotto arborio crémeux au mascarpone",
          description: "Champignons de Paris, coulis de cresson et dentelle de parmesan"
        },
        {
          name: "Noix de Saint-Jacques juste saisies",
          description: "Étuvée de poireaux fondante, sauce porto réduite"
        },
        {
          name: "Paleron de bœuf confit longuement",
          description: "Polenta crémeuse, duo de carottes braisées, jus de cuisson réduit"
        }
      ],
      desserts: [
        {
          name: "Brioche perdue croustillante et moelleuse",
          description: "Caramel au beurre salé, chantilly légère, glace noix de macadamia"
        },
        {
          name: "Tiramisu à la crème de marrons",
          description: "Équilibre de douceur et de gourmandise"
        },
        {
          name: "La douceur de la semaine",
          description: "Création du chef selon l'inspiration et les saisons"
        }
      ]
    }
  };

  // ==================== DINNER MENU DATA ====================
  const dinnerMenus = {
    menuThermes: cmsData?.menu_thermes ? {
      title: cmsData.menu_thermes.title,
      subtitle: cmsData.menu_thermes.subtitle,
      prices: cmsData.menu_thermes.prices,
      miseBouche: cmsData.menu_thermes.mise_en_bouche,
      entrees: cmsData.menu_thermes.starters?.length ? cmsData.menu_thermes.starters : [
        { name: "Terrine de foie gras de canard mi-cuit", description: "confit d'oignons, pain d'épices toasté" }
      ],
      plats: cmsData.menu_thermes.mains?.length ? cmsData.menu_thermes.mains : [
        { name: "Dos de cabillaud skrei", description: "pommes de terre écrasées, huile d'herbes fraîches, lit au vert" }
      ],
      fromages: cmsData.menu_thermes.cheese || { name: "Chariot de fromages de notre région", description: "confiture de figues artisanale" },
      dessert: cmsData.menu_thermes.dessert_note
    } : {
      title: "Menu Thermes",
      subtitle: "Soirée & Week-end",
      prices: [
        { price: "49€", formula: "Entrée – Plat – Dessert" },
        { price: "56€", formula: "Entrée – Plat – Fromage – Dessert" }
      ],
      miseBouche: "Mise en bouche",
      entrees: [
        {
          name: "Terrine de foie gras de canard mi-cuit",
          description: "confit d'oignons, pain d'épices toasté"
        },
        {
          name: "Noix de Saint-Jacques juste saisies",
          description: "étuvée de poireaux fondante, sauce porto"
        }
      ],
      plats: [
        {
          name: "Dos de cabillaud skrei",
          description: "pommes de terre écrasées, huile d'herbes fraîches, lit au vert"
        },
        {
          name: "Cuisses de grenouilles en persillade",
          description: "pommes grenaille, poêlée de légumes du moment, sauce riesling"
        }
      ],
      fromages: {
        name: "Chariot de fromages de notre région",
        description: "confiture de figues artisanale"
      },
      dessert: "Dessert au choix à la carte"
    },
    carte: cmsData?.evening_menu ? {
      entrees: cmsData.evening_menu.starters?.length ? cmsData.evening_menu.starters : [
        { name: "Suprêmes de cailles rôtis, servis tièdes", description: "Saladine de lentilles aux aromates", price: "15€" }
      ],
      plats: cmsData.evening_menu.mains?.length ? cmsData.evening_menu.mains : [
        { name: "Risotto arborio crémeux au mascarpone", description: "Champignons de Paris, coulis de cresson", price: "19€" }
      ],
      desserts: cmsData.evening_menu.desserts?.length ? cmsData.evening_menu.desserts : [
        { name: "Poire pochée", description: "Sauce chocolat, crémeux vanille", price: "9€" }
      ]
    } : {
      entrees: [
        { name: "Suprêmes de cailles rôtis, servis tièdes", description: "Saladine de lentilles aux aromates, mâche, pickles d'oignons rouges, jus corsé au balsamique", price: "15€" },
        { name: "Velouté de potimarron, façon cappuccino", description: "Crème légère, noisettes torréfiées, éclats de châtaignes", price: "14€" },
        { name: "Noix de Saint-Jacques juste saisies", description: "Étuvée de poireaux, sauce porto", price: "17€" },
        { name: "Terrine de foie gras de canard mi-cuit", description: "Confit d'oignons, pain d'épices toasté", price: "20€" }
      ],
      plats: [
        { name: "Risotto arborio crémeux au mascarpone", description: "Champignons de Paris, coulis de cresson, dentelle de parmesan", price: "19€" },
        { name: "Paleron de bœuf confit toute la nuit", description: "Polenta crémeuse, duo de carottes braisées, jus réduit", price: "24€" },
        { name: "Dos de cabillaud skrei snacké", description: "Pommes de terre écrasées à l'huile d'olive et ciboulette, beurre nantais, huile d'herbes", price: "26€" },
        { name: "Filets de perches meunière", description: "Sauce tartare, saladine du moment, pommes frites", price: "28€" },
        { name: "Cuisses de grenouilles en persillade", description: "Pommes grenailles, légumes du moment, sauce au riesling", price: "28€" },
        { name: "Tournedos de bœuf au sautoir", description: "Pressé de pommes de terre au comté, duo de carottes braisées, sauce bordelaise", price: "32€" }
      ],
      desserts: [
        { name: "Poire pochée", description: "Sauce chocolat, crémeux vanille, spéculos", price: "9€" },
        { name: "Vacherin glacé \"Franco-Suisse\"", description: "Myrtille, double crème", price: "11€" },
        { name: "Tiramisu à la crème de marrons", description: "", price: "9€" },
        { name: "Brioche perdue de mon enfance", description: "Chantilly, caramel au beurre salé, glace à la noix de macadamia", price: "9€" },
        { name: "Plateau de fromages affinés de notre région", description: "Confiture de figue", price: "12€" }
      ]
    }
  };

  return (
    <section id="menu" className="min-h-screen bg-[#f5f0e8]">
      <div className="grid lg:grid-cols-[70fr_30fr] xl:grid-cols-[68fr_32fr] min-h-screen">
        {/* Left Side - Menu Content */}
        <div className="py-16 px-6 md:px-12 lg:px-16 xl:px-20 overflow-y-auto">
          <div className="max-w-3xl xl:max-w-4xl">
            {/* Title */}
            <h2 
              className="text-5xl md:text-6xl font-light mb-10 text-gray-900 tracking-[0.15em] uppercase"
              style={{ fontFamily: "var(--font-display)", fontWeight: "400" }}
            >
              La Carte
            </h2>

            {/* Tabs */}
            <div className="flex gap-4 mb-12">
              <button
                onClick={() => setActiveTab('soir')}
                className={`px-8 py-3 font-light text-base tracking-wide transition-all duration-300 ${
                  activeTab === 'soir'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-900 border border-gray-900 hover:bg-gray-100'
                }`}
              >
                Le Soir
              </button>
              <button
                onClick={() => setActiveTab('midi')}
                className={`px-8 py-3 font-light text-base tracking-wide transition-all duration-300 ${
                  activeTab === 'midi'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-900 border border-gray-900 hover:bg-gray-100'
                }`}
              >
                Le Midi
              </button>
            </div>

            {/* ==================== DINNER MENU - SOIR ==================== */}
            {activeTab === 'soir' && (
              <div>
                {/* LA CARTE DU SOIR - EN PREMIER */}
                <div className="mb-16">
                  {/* Header */}
                  <div className="mb-8">
                    <h3 className="text-3xl font-semibold tracking-[0.1em] uppercase text-gray-900 tracking-wide mb-1">
                      La Carte du Soir
                    </h3>
                    <p className="text-base text-gray-600 italic font-light">
                      À la carte
                    </p>
                  </div>

                  {/* Entrées */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-5 text-gray-900 tracking-wide">
                      Entrées
                    </h4>
                    <div className="space-y-4">
                      {dinnerMenus.carte.entrees.map((dish, index) => (
                        <div key={index} className="border-b border-gray-200 pb-4">
                          <div className="flex justify-between items-baseline gap-3 mb-1">
                            <h5 className="text-lg text-gray-900 flex-1" style={{ fontFamily: "var(--font-body)", fontWeight: "400", lineHeight: "1.35" }}>
                              {dish.name}
                            </h5>
                            <span className="text-base text-gray-800 whitespace-nowrap font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                              {dish.price}
                            </span>
                          </div>
                          {dish.description && (
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Plats */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-5 text-gray-900 tracking-wide">
                      Plats
                    </h4>
                    <div className="space-y-4">
                      {dinnerMenus.carte.plats.map((dish, index) => (
                        <div key={index} className="border-b border-gray-200 pb-4">
                          <div className="flex justify-between items-baseline gap-3 mb-1">
                            <h5 className="text-lg text-gray-900 flex-1" style={{ fontFamily: "var(--font-body)", fontWeight: "400", lineHeight: "1.35" }}>
                              {dish.name}
                            </h5>
                            <span className="text-base text-gray-800 whitespace-nowrap font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                              {dish.price}
                            </span>
                          </div>
                          {dish.description && (
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Desserts */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-5 text-gray-900 tracking-wide">
                      Les Gourmandises
                    </h4>
                    <div className="space-y-4">
                      {dinnerMenus.carte.desserts.map((dish, index) => (
                        <div key={index} className="border-b border-gray-200 pb-4">
                          <div className="flex justify-between items-baseline gap-3 mb-1">
                            <h5 className="text-lg text-gray-900 flex-1" style={{ fontFamily: "var(--font-body)", fontWeight: "400", lineHeight: "1.35" }}>
                              {dish.name}
                            </h5>
                            <span className="text-base text-gray-800 whitespace-nowrap font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                              {dish.price}
                            </span>
                          </div>
                          {dish.description && (
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button - Dynamic PDF from CMS for Evening Menu */}
                  {cmsData?.evening_menu?.pdf_url && cmsData?.evening_menu?.show_pdf !== false && (
                    <div className="text-center mt-8">
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cmsData.evening_menu.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gray-900 text-white px-8 py-3 text-sm tracking-wide hover:bg-gray-800 transition-colors duration-300"
                      >
                        Consulter la carte complète
                      </a>
                    </div>
                  )}
                </div>

                {/* Elegant Divider */}
                <div className="flex items-center justify-center mb-14">
                  <div className="w-12 h-px bg-gray-400"></div>
                  <div className="mx-4 text-gray-400 text-xl font-light">✦</div>
                  <div className="w-12 h-px bg-gray-400"></div>
                </div>

                {/* MENU THERMES - EN SECOND */}
                <div className="mb-14">
                  {/* Header */}
                  <div className="mb-6">
                    <h3 className="text-3xl font-semibold tracking-[0.1em] uppercase text-gray-900 tracking-wide mb-1">
                      {dinnerMenus.menuThermes.title}
                    </h3>
                    <p className="text-base text-gray-600 italic font-light">
                      {dinnerMenus.menuThermes.subtitle}
                    </p>
                  </div>

                  {/* Mise en bouche */}
                  <div className="mb-6 border-b border-gray-300 pb-4">
                    <p className="text-base text-gray-700 italic font-light">
                      {dinnerMenus.menuThermes.miseBouche}
                    </p>
                  </div>

                  {/* Entrées au choix */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-4 text-gray-900 tracking-wide">
                      Entrées au choix
                    </h4>
                    <div className="space-y-3">
                      {dinnerMenus.menuThermes.entrees.map((dish, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <p className="text-center text-gray-400 italic font-light my-3 text-sm">
                              ou
                            </p>
                          )}
                          <div className="border-b border-gray-200 pb-4">
                            <h5 className="font-light text-lg text-gray-900 mb-1">
                              {dish.name}
                            </h5>
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Plats au choix */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-4 text-gray-900 tracking-wide">
                      Plats au choix
                    </h4>
                    <div className="space-y-3">
                      {dinnerMenus.menuThermes.plats.map((dish, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <p className="text-center text-gray-400 italic font-light my-3 text-sm">
                              ou
                            </p>
                          )}
                          <div className="border-b border-gray-200 pb-4">
                            <h5 className="font-light text-lg text-gray-900 mb-1">
                              {dish.name}
                            </h5>
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Fromages */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-4 text-gray-900 tracking-wide">
                      Fromages
                    </h4>
                    <div className="border-b border-gray-200 pb-4">
                      <h5 className="font-light text-lg text-gray-900 mb-1">
                        {dinnerMenus.menuThermes.fromages.name}
                      </h5>
                      <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                        {dinnerMenus.menuThermes.fromages.description}
                      </p>
                    </div>
                  </div>

                  {/* Dessert */}
                  <div className="mb-6">
                    <p className="text-base text-gray-700 italic font-light">
                      {dinnerMenus.menuThermes.dessert}
                    </p>
                  </div>

                  {/* Prices - at the end */}
                  <div className="space-y-1 mt-8 pt-6 border-t border-gray-300">
                    {dinnerMenus.menuThermes.prices.map((p, idx) => (
                      <div key={idx} className="flex items-baseline gap-3">
                        <span className="font-semibold text-xl text-gray-900">{p.price}</span>
                        <span className="text-sm text-gray-600 font-light">{p.formula}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button - Dynamic PDF from CMS for Menu Thermes */}
                  {cmsData?.menu_thermes?.pdf_url && cmsData?.menu_thermes?.show_pdf !== false && (
                    <div className="text-center mt-8">
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cmsData.menu_thermes.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gray-900 text-white px-8 py-3 text-sm tracking-wide hover:bg-gray-800 transition-colors duration-300"
                      >
                        Consulter le menu complet
                      </a>
                    </div>
                  )}
                </div>

                {/* Chef Signature */}
                <div className="mt-10">
                  <p className="text-sm text-gray-500 italic font-light">
                    Chef Pascal Aguettaz
                  </p>
                </div>
              </div>
            )}

            {/* ==================== LUNCH MENU - MIDI ==================== */}
            {activeTab === 'midi' && (
              <div>
                {/* MENU DE LA SEMAINE */}
                <div className="mb-24">
                  {/* Header */}
                  <div className="mb-10">
                    <div className="flex justify-between items-baseline mb-2">
                      <h3 className="text-3xl font-semibold tracking-[0.1em] uppercase text-gray-900 tracking-wide">
                        {lunchMenus.weeklyMenu.title}
                      </h3>
                      <span className="font-semibold text-2xl text-gray-900">
                        {lunchMenus.weeklyMenu.price}
                      </span>
                    </div>
                    <p className="text-base text-gray-600 italic font-light">
                      {lunchMenus.weeklyMenu.subtitle}
                    </p>
                  </div>

                  {/* Entrée */}
                  <div className="mb-10 border-b border-gray-200 pb-8">
                    <h4 className="text-lg uppercase tracking-widest mb-4 font-bold" style={{ color: "var(--color-bleu-nuit)" }}>
                      {lunchMenus.weeklyMenu.entree.title}
                    </h4>
                    <h5 className="text-xl mb-2" style={{ fontFamily: "var(--font-body)", fontWeight: "500", color: "var(--color-bleu-nuit)" }}>
                      {lunchMenus.weeklyMenu.entree.name}
                    </h5>
                    <p className="text-xl" style={{ fontFamily: "var(--font-body)", lineHeight: "1.9", color: "var(--color-gris-bleu)", fontSize: "clamp(18px, 1.9vw, 21px)" }}>
                      {lunchMenus.weeklyMenu.entree.description}
                    </p>
                  </div>

                  {/* Plat */}
                  <div className="mb-10 border-b border-gray-200 pb-8">
                    <h4 className="text-lg uppercase tracking-widest mb-4 font-bold" style={{ color: "var(--color-bleu-nuit)" }}>
                      {lunchMenus.weeklyMenu.plat.title}
                    </h4>
                    <h5 className="text-xl mb-2" style={{ fontFamily: "var(--font-body)", fontWeight: "500", color: "var(--color-bleu-nuit)" }}>
                      {lunchMenus.weeklyMenu.plat.name}
                    </h5>
                    <p className="text-xl" style={{ fontFamily: "var(--font-body)", lineHeight: "1.9", color: "var(--color-gris-bleu)", fontSize: "clamp(18px, 1.9vw, 21px)" }}>
                      {lunchMenus.weeklyMenu.plat.description}
                    </p>
                  </div>

                  {/* Dessert */}
                  <div className="mb-10 border-b border-gray-200 pb-8">
                    <h4 className="text-lg uppercase tracking-widest mb-4 font-bold" style={{ color: "var(--color-bleu-nuit)" }}>
                      {lunchMenus.weeklyMenu.dessert.title}
                    </h4>
                    <h5 className="text-xl mb-2" style={{ fontFamily: "var(--font-body)", fontWeight: "500", color: "var(--color-bleu-nuit)" }}>
                      {lunchMenus.weeklyMenu.dessert.name}
                    </h5>
                    <p className="text-xl" style={{ fontFamily: "var(--font-body)", lineHeight: "1.9", color: "var(--color-gris-bleu)", fontSize: "clamp(18px, 1.9vw, 21px)" }}>
                      {lunchMenus.weeklyMenu.dessert.description}
                    </p>
                  </div>

                  {/* Note */}
                  <p className="text-sm text-gray-500 italic font-light mb-8">
                    {lunchMenus.weeklyMenu.note}
                  </p>

                  {/* CTA Button - Dynamic PDF from CMS */}
                  {cmsData?.weekly_menu?.pdf_url && cmsData?.weekly_menu?.show_pdf !== false && (
                    <div className="text-center">
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cmsData.weekly_menu.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gray-900 text-white px-8 py-3 text-sm tracking-wide hover:bg-gray-800 transition-colors duration-300"
                      >
                        Consulter le menu complet
                      </a>
                    </div>
                  )}
                </div>

                {/* Elegant Divider */}
                <div className="flex items-center justify-center mb-14">
                  <div className="w-12 h-px bg-gray-400"></div>
                  <div className="mx-4 text-gray-400 text-xl font-light">✦</div>
                  <div className="w-12 h-px bg-gray-400"></div>
                </div>

                {/* MENU SOURCE */}
                <div className="mb-14">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex justify-between items-baseline mb-1">
                      <h3 className="text-3xl font-semibold tracking-[0.1em] uppercase text-gray-900 tracking-wide">
                        {lunchMenus.menuSource.title}
                      </h3>
                      <span className="font-semibold text-xl text-gray-900">
                        {lunchMenus.menuSource.price}
                      </span>
                    </div>
                    <p className="text-base text-gray-600 italic font-light">
                      {lunchMenus.menuSource.subtitle}
                    </p>
                  </div>

                  {/* Entrées */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-4 text-gray-900 tracking-wide">
                      Entrées
                    </h4>
                    <div className="space-y-3">
                      {lunchMenus.menuSource.entrees.map((dish, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <p className="text-center text-gray-400 italic font-light my-3 text-sm">
                              ou
                            </p>
                          )}
                          <div className="border-b border-gray-200 pb-4">
                            <h5 className="font-light text-lg text-gray-900 mb-1">
                              {dish.name}
                            </h5>
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Plats */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-4 text-gray-900 tracking-wide">
                      Plats
                    </h4>
                    <div className="space-y-3">
                      {lunchMenus.menuSource.plats.map((dish, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <p className="text-center text-gray-400 italic font-light my-3 text-sm">
                              ou
                            </p>
                          )}
                          <div className="border-b border-gray-200 pb-4">
                            <h5 className="font-light text-lg text-gray-900 mb-1">
                              {dish.name}
                            </h5>
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Desserts */}
                  <div className="mb-8">
                    <h4 className="text-xl font-semibold tracking-[0.08em] uppercase mb-4 text-gray-900 tracking-wide">
                      Desserts
                    </h4>
                    <div className="space-y-3">
                      {lunchMenus.menuSource.desserts.map((dish, index) => (
                        <div key={index}>
                          {index > 0 && (
                            <p className="text-center text-gray-400 italic font-light my-3 text-sm">
                              ou
                            </p>
                          )}
                          <div className="border-b border-gray-200 pb-4">
                            <h5 className="font-light text-lg text-gray-900 mb-1">
                              {dish.name}
                            </h5>
                            <p className="text-base text-gray-600" style={{ fontFamily: "var(--font-body)", lineHeight: "1.6" }}>
                              {dish.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button - Dynamic PDF from CMS */}
                  {cmsData?.menu_source?.pdf_url && cmsData?.menu_source?.show_pdf !== false && (
                    <div className="text-center">
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${cmsData.menu_source.pdf_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gray-900 text-white px-8 py-3 text-sm tracking-wide hover:bg-gray-800 transition-colors duration-300"
                      >
                        Consulter le menu complet
                      </a>
                    </div>
                  )}
                </div>

                {/* Chef Signature */}
                <div className="mt-10">
                  <p className="text-sm text-gray-500 italic font-light">
                    Chef Pascal Aguettaz
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Image - Fixed, no zoom, stable framing */}
        <div className="hidden lg:block relative">
          <div className="sticky top-0 h-screen">
            <div 
              className="w-full h-full"
              style={{
                backgroundImage: 'url(https://images.pexels.com/photos/32399759/pexels-photo-32399759.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
              }}
              role="img"
              aria-label="Plat gastronomique La Rotonde"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
